import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  SelectChangeEvent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Switch,
  FormControlLabel,
  Slider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import productsService, {
  type MassConcreteDefaultDetail,
  type MassConcreteDefaultLimit,
  type MassConcreteDefaultLimitInput,
} from '../services/products';
import {
  TemperatureProfileBar,
  DEFAULT_SCALE_MAX,
  type TempLimitMarker,
} from '../components/dashboard/TemperatureProfileBar';

type Mode = 'view' | 'edit' | 'create';

// Criteria codes mirror the legacy GetCriteriaJSON in OperationHandler.aspx.cs.
// 01 = Temperature Differential, 02 = Temperature (only). 03 is a ConXedge
// addition: the "Temperature Bar Indicator" row, whose temperature defines the
// max of the temperature bar shown on the Control Panel / Pour Dashboard. It is
// excluded from the coloured limit markers and carries no purpose/colour.
const CRITERIA_TEMP_DIFF = '01';
const CRITERIA_TEMP = '02';
const CRITERIA_BAR_INDICATOR = '03';

const CRITERIA_OPTIONS: { value: string; label: string }[] = [
  { value: CRITERIA_TEMP, label: 'Temperature' },
  { value: CRITERIA_BAR_INDICATOR, label: 'Temperature Bar' },
  { value: CRITERIA_TEMP_DIFF, label: 'Temperature Differential' },
];

const LIMIT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '01', label: 'Greater Than' },
  { value: '02', label: 'Greater than and equal to' },
  { value: '03', label: 'Less than' },
  { value: '04', label: 'Less than and equal to' },
];

const labelFromOptions = (
  options: { value: string; label: string }[],
  value: string,
): string => options.find((o) => o.value === value)?.label || value;

// ---------------------------------------------------------------------------
// Form state types
// ---------------------------------------------------------------------------

interface DefaultFormState {
  description: string;
  default_id: string;
}

const EMPTY_DEFAULT: DefaultFormState = {
  description: '',
  default_id: '',
};

/** A draft limit row held in component state until the whole group is saved.
 *  `id` is present for rows loaded from the server (drives diff/upsert) and
 *  absent for rows added in the UI. */
interface LimitFormState {
  id?: string;
  criteria: string;
  purpose: string;
  limits_type: string;
  limits_temp: string;
  background_color: string;
  text_color: string;
}

const EMPTY_LIMIT: LimitFormState = {
  criteria: CRITERIA_TEMP,
  purpose: '',
  limits_type: '01',
  limits_temp: '',
  background_color: '#ffffff',
  text_color: '#000000',
};

/** Default Temperature Bar Indicator seeded into every new group (110 °C). */
const DEFAULT_BAR_INDICATOR_TEMP = '110';
const BAR_INDICATOR_PURPOSE = 'Bar Scale Maximum';
const makeDefaultBarIndicator = (): LimitFormState => ({
  criteria: CRITERIA_BAR_INDICATOR,
  purpose: BAR_INDICATOR_PURPOSE,
  limits_type: '01',
  limits_temp: DEFAULT_BAR_INDICATOR_TEMP,
  background_color: '#ffffff',
  text_color: '#000000',
});

/** Simple indicator-colour palette (bg + auto-contrast text). */
const COLOR_PRESETS: { label: string; bg: string; fg: string }[] = [
  { label: 'Green', bg: '#2E7D32', fg: '#FFFFFF' },
  { label: 'Lime', bg: '#9CCC65', fg: '#000000' },
  { label: 'Yellow', bg: '#F5D800', fg: '#000000' },
  { label: 'Amber', bg: '#FFB300', fg: '#000000' },
  { label: 'Orange', bg: '#F57C00', fg: '#FFFFFF' },
  { label: 'Red', bg: '#D32F2F', fg: '#FFFFFF' },
  { label: 'Dark Red', bg: '#8B0000', fg: '#FFFFFF' },
  { label: 'Blue', bg: '#1565C0', fg: '#FFFFFF' },
  { label: 'Purple', bg: '#6A1B9A', fg: '#FFFFFF' },
];

const limitToForm = (l: MassConcreteDefaultLimit): LimitFormState => ({
  id: l.id,
  criteria: l.criteria || CRITERIA_TEMP,
  purpose: l.purpose || '',
  limits_type: l.limits_type || '01',
  limits_temp:
    l.limits_temp !== undefined && l.limits_temp !== null ? String(l.limits_temp) : '',
  background_color: l.background_color || '#ffffff',
  text_color: l.text_color || '#000000',
});

const formToInput = (f: LimitFormState): MassConcreteDefaultLimitInput => {
  const isBar = f.criteria === CRITERIA_BAR_INDICATOR;
  return {
    id: f.id,
    criteria: f.criteria,
    // Keep the bar indicator's descriptive purpose; it just isn't user-edited.
    purpose: f.purpose.trim(),
    limits_type: f.limits_type,
    limits_temp: Number(f.limits_temp),
    background_color: isBar ? undefined : f.background_color || undefined,
    text_color: isBar ? undefined : f.text_color || undefined,
  };
};

const formatDateTime = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MassConcreteLimitForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const mode: Mode = id == null
    ? 'create'
    : location.pathname.endsWith('/edit')
      ? 'edit'
      : 'view';

  const isView = mode === 'view';
  const isCreate = mode === 'create';
  const canManage = user?.UserType === '01';
  const readOnly = isView || !canManage;

  // Parent (group) fields
  const [defaultForm, setDefaultForm] = useState<DefaultFormState>(EMPTY_DEFAULT);
  const [isActive, setIsActive] = useState(true);
  const [detail, setDetail] = useState<MassConcreteDefaultDetail | null>(null);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Draft child limit rows (saved together with the parent)
  // New groups start with a default Temperature Bar Indicator (110 °C); existing
  // groups load their rows from the server in the effect below.
  const [limits, setLimits] = useState<LimitFormState[]>(
    isCreate ? [makeDefaultBarIndicator()] : [],
  );

  // Simulated pour temperature for the live preview bar (drag to scrub).
  const [previewTemp, setPreviewTemp] = useState<number>(55);

  const [limitDialog, setLimitDialog] = useState<{
    open: boolean;
    editingIndex: number | null;
    form: LimitFormState;
  }>({ open: false, editingIndex: null, form: EMPTY_LIMIT });
  const [limitDeleteIndex, setLimitDeleteIndex] = useState<number | null>(null);

  // ---- Initial load (edit/view) ----
  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const d = await productsService.getMassConcreteDefault(id);
        if (cancelled) return;
        setDetail(d);
        setDefaultForm({ description: d.description || '', default_id: d.default_id || '' });
        setIsActive(d.is_active ?? true);
        setLimits((d.limits || []).map(limitToForm));
      } catch (e: any) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleField = (field: keyof DefaultFormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setDefaultForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  // ---- Whole-group save (parent + all limit rows in one request) ----
  const handleSave = async () => {
    if (!defaultForm.description.trim()) {
      setSnackbar({ msg: t('massConcreteLimit.descriptionRequired', 'Description is required'), severity: 'error' });
      return;
    }
    if (!user?.CompanyID) {
      setSnackbar({ msg: 'Missing company context', severity: 'error' });
      return;
    }

    setSaving(true);
    setError('');
    try {
      const limitsPayload = limits.map(formToInput);
      const groupCode = defaultForm.default_id.trim();
      if (isCreate) {
        const created = await productsService.createMassConcreteDefault({
          company_id: user.CompanyID,
          description: defaultForm.description.trim(),
          default_id: groupCode || undefined,
          is_active: isActive,
          limits: limitsPayload,
        });
        setSnackbar({ msg: t('massConcreteLimit.created', 'Temperature limit group created'), severity: 'success' });
        navigate(`/mass-concrete-limits/${created.id}`, { replace: true });
      } else if (id) {
        await productsService.updateMassConcreteDefault(id, {
          description: defaultForm.description.trim(),
          default_id: groupCode || undefined,
          is_active: isActive,
          limits: limitsPayload,
        });
        setSnackbar({ msg: t('massConcreteLimit.updated', 'Temperature limit group updated'), severity: 'success' });
        navigate(`/mass-concrete-limits/${id}`, { replace: true });
      }
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---- Limit dialog (operates on draft state, no API calls) ----
  const openCreateLimit = () =>
    setLimitDialog({ open: true, editingIndex: null, form: EMPTY_LIMIT });

  const openEditLimit = (index: number) =>
    setLimitDialog({ open: true, editingIndex: index, form: { ...limits[index] } });

  const closeLimitDialog = () => setLimitDialog((prev) => ({ ...prev, open: false }));

  const setLimitField = (field: keyof LimitFormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setLimitDialog((prev) => ({ ...prev, form: { ...prev.form, [field]: e.target.value } }));
    };

  const setLimitSelect = (field: keyof LimitFormState) => (e: SelectChangeEvent) => {
    setLimitDialog((prev) => ({ ...prev, form: { ...prev.form, [field]: e.target.value as string } }));
  };

  /** Pick a preset swatch — sets background + auto-contrast text together. */
  const selectColor = (bg: string, fg: string) =>
    setLimitDialog((prev) => ({
      ...prev,
      form: { ...prev.form, background_color: bg, text_color: fg },
    }));

  const handleSaveLimit = () => {
    const f = limitDialog.form;
    const isBar = f.criteria === CRITERIA_BAR_INDICATOR;
    if (!f.criteria) {
      setSnackbar({ msg: t('massConcreteLimit.criteriaRequired', 'Criteria is required'), severity: 'error' });
      return;
    }
    if (!isBar && !f.purpose.trim()) {
      setSnackbar({ msg: t('massConcreteLimit.purposeRequired', 'Purpose is required'), severity: 'error' });
      return;
    }
    if (f.limits_temp === '' || Number.isNaN(Number(f.limits_temp))) {
      setSnackbar({ msg: t('massConcreteLimit.tempRequired', 'Limit temperature is required'), severity: 'error' });
      return;
    }
    setLimits((prev) => {
      const next = [...prev];
      if (limitDialog.editingIndex != null) {
        next[limitDialog.editingIndex] = f;
      } else {
        next.push(f);
      }
      return next;
    });
    closeLimitDialog();
  };

  const handleDeleteLimit = () => {
    if (limitDeleteIndex == null) return;
    setLimits((prev) => prev.filter((_, i) => i !== limitDeleteIndex));
    setLimitDeleteIndex(null);
  };

  // ---- Preview bar inputs ----
  const previewMarkers: TempLimitMarker[] = limits
    .filter((l) => l.criteria === CRITERIA_TEMP && l.limits_temp !== '' && Number(l.limits_temp) > 0)
    .map((l) => ({
      temp: Number(l.limits_temp),
      color: l.background_color || undefined,
      label: l.purpose || undefined,
    }));

  const previewScaleMax = (() => {
    const bar = limits.find(
      (l) => l.criteria === CRITERIA_BAR_INDICATOR && Number(l.limits_temp) > 0,
    );
    return bar ? Number(bar.limits_temp) : DEFAULT_SCALE_MAX;
  })();

  const titleByMode = () => {
    if (isCreate) return t('massConcreteLimit.addTitle', 'Create new Temperature Only Limit Group');
    if (isView) return t('massConcreteLimit.viewTitle', 'Temperature Only Limit Group');
    return t('massConcreteLimit.editTitle', 'Edit Temperature Only Limit Group');
  };

  // ---- Indicator cell (coloured chip; plain bold for the bar indicator) ----
  const renderIndicator = (l: LimitFormState) => {
    const isBar = l.criteria === CRITERIA_BAR_INDICATOR;
    const hasColor = !isBar && !!l.background_color && l.background_color !== '#ffffff';
    const val = l.limits_temp !== '' ? `${Number(l.limits_temp).toFixed(1)} °C` : '-';
    return (
      <Box
        sx={{
          display: 'inline-block',
          backgroundColor: hasColor ? l.background_color : 'transparent',
          color: hasColor ? l.text_color || '#000' : 'inherit',
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          minWidth: 64,
          textAlign: 'center',
          fontWeight: 700,
          border: hasColor ? '1px solid rgba(0,0,0,0.08)' : 'none',
        }}
      >
        {val}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const dialogIsBar = limitDialog.form.criteria === CRITERIA_BAR_INDICATOR;
  // The Bar Indicator is seeded by default (one per group), so it's hidden from
  // the "Add Limit" dropdown — only shown when editing the existing one.
  const criteriaOptions = dialogIsBar
    ? CRITERIA_OPTIONS
    : CRITERIA_OPTIONS.filter((o) => o.value !== CRITERIA_BAR_INDICATOR);

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, sm: 3 }, py: 2 }}>
      {/* Top bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/mass-concrete-limits')}>
          {t('massConcreteLimit.backToList', 'Back to List')}
        </Button>
        {isView && id && canManage && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/mass-concrete-limits/${id}/edit`)}
          >
            {t('common.edit', 'Edit')}
          </Button>
        )}
      </Box>

      <Typography variant="h5" sx={{ mb: 2 }}>{titleByMode()}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2}>
        {/* ---------- Main column ---------- */}
        <Grid size={{ xs: 12, md: detail ? 9 : 12 }}>
          {/* Group header card */}
          <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    disabled={readOnly}
                  />
                }
                label={t('massConcreteLimit.groupActive', 'Temperature Limit Group:')}
                labelPlacement="start"
                sx={{ ml: 0 }}
              />
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('massConcreteLimit.groupCode', 'Group Code')}
                  value={defaultForm.default_id}
                  onChange={handleField('default_id')}
                  disabled={readOnly}
                  helperText={isCreate
                    ? t('massConcreteLimit.groupCodeHint', 'Auto-generated if left blank')
                    : undefined}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 7 }}>
                <TextField
                  fullWidth
                  size="small"
                  required
                  label={t('massConcreteLimit.groupDescription', 'Group Description')}
                  value={defaultForm.description}
                  onChange={handleField('description')}
                  disabled={readOnly}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Temperature Limits card */}
          <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
            <Box>
              <Typography variant="h6">
                {t('massConcreteLimit.tempLimitsSection', 'Temperature Limits')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(
                  'massConcreteLimit.limitsHint',
                  'Temperature and temperature differential thresholds applied to pours using this default.',
                )}
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />

            <Table size="small">
              <TableHead>
                <TableRow>
                  {!readOnly && <TableCell sx={{ fontWeight: 600 }}>{t('common.actions', 'Action')}</TableCell>}
                  <TableCell sx={{ fontWeight: 600 }}>{t('massConcreteLimit.criteria', 'Criteria')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('massConcreteLimit.purpose', 'Purpose')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('massConcreteLimit.sign', 'Sign')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">{t('massConcreteLimit.indicator', 'Indicator')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {limits.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={readOnly ? 4 : 5}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        {t('massConcreteLimit.noLimitsYet', 'No limits added yet.')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  limits.map((l, idx) => (
                    <TableRow key={l.id ?? `draft-${idx}`}>
                      {!readOnly && (
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <IconButton size="small" color="primary" onClick={() => openEditLimit(idx)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {l.criteria !== CRITERIA_BAR_INDICATOR && (
                            <IconButton size="small" onClick={() => setLimitDeleteIndex(idx)}>
                              <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                            </IconButton>
                          )}
                        </TableCell>
                      )}
                      <TableCell>{labelFromOptions(CRITERIA_OPTIONS, l.criteria)}</TableCell>
                      <TableCell>{l.purpose}</TableCell>
                      <TableCell>{l.criteria === CRITERIA_BAR_INDICATOR ? '' : labelFromOptions(LIMIT_TYPE_OPTIONS, l.limits_type)}</TableCell>
                      <TableCell align="right">{renderIndicator(l)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {!readOnly && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<AddIcon />}
                  onClick={openCreateLimit}
                  sx={{ textTransform: 'none' }}
                >
                  {t('massConcreteLimit.addLimit', 'Add Limit')}
                </Button>
              </Box>
            )}
          </Paper>

          {/* Preview card */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t('massConcreteLimit.previewTitle', 'Preview - Temperature Indicator Bar')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {previewTemp} °C
              </Typography>
            </Box>
            <TemperatureProfileBar
              currentTemp={previewTemp}
              maxTemp={previewTemp}
              referenceMax={previewScaleMax}
              limits={previewMarkers}
              scaleMax={previewScaleMax}
              showScale
              forceScale
              animate={false}
              sx={{ height: 28 }}
            />
            {/* Draggable simulated pour temperature */}
            <Box sx={{ px: 0.5, mt: 1.5 }}>
              <Slider
                value={Math.min(previewTemp, previewScaleMax)}
                onChange={(_e, v) => setPreviewTemp(v as number)}
                min={0}
                max={previewScaleMax}
                step={1}
                aria-label={t('massConcreteLimit.simulatedTemp', 'Simulated temperature')}
                sx={{ color: 'grey.800' }}
              />
              {/* Value label tracking under the thumb (no pop-up over the bar) */}
              <Box sx={{ position: 'relative', height: 14, mt: -0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    left: `${(Math.min(previewTemp, previewScaleMax) / (previewScaleMax || 1)) * 100}%`,
                    transform: 'translateX(-50%)',
                    fontWeight: 700,
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {previewTemp} °C
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {t('massConcreteLimit.simulatedTempHint', 'Slide to preview temperature')}
              </Typography>
            </Box>
          </Paper>

          {/* Save / cancel */}
          {!readOnly && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
              <Button onClick={() => navigate('/mass-concrete-limits')} disabled={saving}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? t('common.saving', 'Saving...')
                  : isCreate
                    ? t('common.create', 'Create')
                    : t('common.save', 'Save')}
              </Button>
            </Box>
          )}
        </Grid>

        {/* ---------- History sidebar (existing groups only) ---------- */}
        {detail && (
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper elevation={2} sx={{ p: 2.5 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('massConcreteLimit.dateCreated', 'Date Created')}:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatDateTime(detail.date_create)}
                  {detail.create_by ? ` (${detail.create_by})` : ''}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('massConcreteLimit.dateLastChanged', 'Date Last Changed')}:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatDateTime(detail.date_last_modified)}
                  {detail.modified_by ? ` (${detail.modified_by})` : ''}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" color="text.secondary">
                {t('massConcreteLimit.history', 'History')}
              </Typography>
              <Box sx={{ mt: 1.5 }}>
                {[
                  {
                    label: t('massConcreteLimit.historyCreated', 'Group Created'),
                    when: detail.date_create,
                    who: detail.create_by,
                  },
                  {
                    label: t('massConcreteLimit.historyChanged', 'Last Changed'),
                    when: detail.date_last_modified,
                    who: detail.modified_by,
                  },
                ].map((ev, i, arr) => (
                  <Box key={ev.label} sx={{ display: 'flex', gap: 1.5 }}>
                    {/* dot + connector */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                          mt: 0.5,
                        }}
                      />
                      {i < arr.length - 1 && (
                        <Box sx={{ width: '2px', flex: 1, bgcolor: 'grey.300', my: 0.5 }} />
                      )}
                    </Box>
                    <Box sx={{ pb: 2 }}>
                      <Typography variant="body2" fontWeight={600}>{ev.label}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {formatDateTime(ev.when)}
                      </Typography>
                      {ev.who && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {ev.who}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* ---------- Limit add/edit dialog ---------- */}
      <Dialog open={limitDialog.open} onClose={closeLimitDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {limitDialog.editingIndex != null
            ? t('massConcreteLimit.editLimit', 'Edit Limit')
            : t('massConcreteLimit.addLimit', 'Add Limit')}
        </DialogTitle>
        <DialogContent sx={{ '&.MuiDialogContent-root': { pt: 3 } }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="limit-criteria-label" shrink>
                  {t('massConcreteLimit.criteria', 'Criteria')}
                </InputLabel>
                <Select
                  labelId="limit-criteria-label"
                  label={t('massConcreteLimit.criteria', 'Criteria')}
                  value={limitDialog.form.criteria}
                  onChange={setLimitSelect('criteria')}
                >
                  {criteriaOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {!dialogIsBar && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  required
                  label={t('massConcreteLimit.purpose', 'Purpose')}
                  value={limitDialog.form.purpose}
                  onChange={setLimitField('purpose')}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
            )}
            {!dialogIsBar && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="limit-type-label" shrink>
                    {t('massConcreteLimit.sign', 'Sign')}
                  </InputLabel>
                  <Select
                    labelId="limit-type-label"
                    label={t('massConcreteLimit.sign', 'Sign')}
                    value={limitDialog.form.limits_type}
                    onChange={setLimitSelect('limits_type')}
                  >
                    {LIMIT_TYPE_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                required
                type="number"
                label={t('massConcreteLimit.tempC', 'Temp (°C)')}
                value={limitDialog.form.limits_temp}
                onChange={setLimitField('limits_temp')}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            {!dialogIsBar && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  {t('massConcreteLimit.colour', 'Indicator colour')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                  {COLOR_PRESETS.map((p) => {
                    const selected =
                      (limitDialog.form.background_color || '').toLowerCase() === p.bg.toLowerCase();
                    return (
                      <Box
                        key={p.bg}
                        role="button"
                        title={p.label}
                        onClick={() => selectColor(p.bg, p.fg)}
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          cursor: 'pointer',
                          background: `linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 55%), ${p.bg}`,
                          border: selected ? '2px solid #1976d2' : '1px solid rgba(0,0,0,0.2)',
                          boxShadow: selected ? '0 0 0 2px #fff inset' : 'none',
                          transition: 'transform 0.1s ease',
                          '&:hover': { transform: 'scale(1.12)' },
                        }}
                      />
                    );
                  })}
                </Box>
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.25,
                    borderRadius: 1,
                    backgroundColor: limitDialog.form.background_color,
                    color: limitDialog.form.text_color,
                    textAlign: 'center',
                    fontWeight: 700,
                    border: '1px solid rgba(0,0,0,0.12)',
                  }}
                >
                  {limitDialog.form.limits_temp
                    ? `${limitDialog.form.limits_temp} °C`
                    : t('massConcreteLimit.preview', 'Preview')}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLimitDialog}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" onClick={handleSaveLimit} startIcon={<SaveIcon />}>
            {t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- Limit delete confirmation ---------- */}
      <Dialog
        open={limitDeleteIndex !== null}
        onClose={() => setLimitDeleteIndex(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('massConcreteLimit.deleteLimitTitle', 'Delete limit?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('massConcreteLimit.deleteLimitBody', {
              defaultValue: 'Delete the "{{label}}" limit? It is removed when you save the group.',
              label:
                limitDeleteIndex != null
                  ? (limits[limitDeleteIndex]?.purpose
                    || labelFromOptions(CRITERIA_OPTIONS, limits[limitDeleteIndex]?.criteria || ''))
                  : '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitDeleteIndex(null)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteLimit} startIcon={<DeleteIcon />}>
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
      >
        <Alert
          severity={snackbar?.severity ?? 'success'}
          onClose={() => setSnackbar(null)}
          variant="filled"
        >
          {snackbar?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MassConcreteLimitForm;
